import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, computed, DestroyRef, effect, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { SplashScreen } from '@capacitor/splash-screen';

import { GlanceService, ProfileService } from '@ld.consumer-portal/data-access';
// eslint-disable-next-line @nx/enforce-module-boundaries -- Modal components are used in the root app template and must be statically imported
import {
  ChunkErrorDialogComponent,
  ErrorModalComponent,
  ForceUpdateModalComponent,
  MaintenanceModalComponent,
  OfflineScreenComponent,
  SessionTimeoutModalComponent,
} from '@ld.consumer-portal/modals';
import { ENVIRONMENT, isValidUserProfile } from '@ld.consumer-portal/models';
// eslint-disable-next-line @nx/enforce-module-boundaries -- These components are used in the root app template and must be statically imported
import { ChatCardComponent, ToastContainerComponent } from '@ld.consumer-portal/ui-core';
import {
  AnalyticsService,
  AuthService,
  BrowserService,
  ChunkErrorService,
  CiamAuthService,
  CookieConsentService,
  CrashlyticsService,
  CustomerIdentityService,
  CustomErrorModalService,
  DynatraceService,
  FirebaseService,
  ForceUpdateService,
  KeyboardService,
  LoggingService,
  NativeNavigationService,
  OfflineDetectionService,
  OtaUpdateService,
  PlatformService,
  RemoteConfigService,
  ServisBotService,
  StatusBarService,
  ThemeService,
  TokenRefreshService,
  TourGuideService,
  UserIdleService,
} from '@ld.consumer-portal/utils';
import { filter, map, startWith } from 'rxjs/operators';

@Component({
  imports: [
    RouterModule,
    ChunkErrorDialogComponent,
    SessionTimeoutModalComponent,
    ErrorModalComponent,
    ForceUpdateModalComponent,
    MaintenanceModalComponent,
    OfflineScreenComponent,
    ToastContainerComponent,
    ChatCardComponent,
  ],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  private router = inject(Router);
  private platformService = inject(PlatformService);
  protected authService = inject(AuthService);
  protected userIdleService = inject(UserIdleService);
  private tokenRefreshService = inject(TokenRefreshService);
  private nativeNavigationService = inject(NativeNavigationService);
  private analyticsService = inject(AnalyticsService);
  private dynatraceService = inject(DynatraceService);
  private loggingService = inject(LoggingService);
  private env = inject(ENVIRONMENT);
  private ciamAuthService = inject(CiamAuthService);
  private customerIdentityService = inject(CustomerIdentityService);
  private destroyRef = inject(DestroyRef);
  protected servisBotService = inject(ServisBotService);
  protected chunkErrorService = inject(ChunkErrorService);
  protected customErrorModalService = inject(CustomErrorModalService);
  private statusBarService = inject(StatusBarService);
  protected remoteConfigService = inject(RemoteConfigService);
  protected forceUpdateService = inject(ForceUpdateService);
  private themeService = inject(ThemeService);
  protected firebaseService = inject(FirebaseService);
  private keyboardService = inject(KeyboardService);
  private cookieConsentService = inject(CookieConsentService);
  private otaUpdateService = inject(OtaUpdateService);
  protected offlineDetectionService = inject(OfflineDetectionService);
  private browserService = inject(BrowserService);
  private tourGuideService = inject(TourGuideService);
  private crashlyticsService = inject(CrashlyticsService);
  private profileService = inject(ProfileService);
  private glanceService = inject(GlanceService);

  private readonly breakpointObserver = inject(BreakpointObserver);

  protected title = 'consumer-portal';

  private readonly isNativeMobile = this.platformService.isNativeMobile;

  private readonly isMobileViewport = toSignal(
    this.breakpointObserver.observe('(max-width: 767px)').pipe(map((result) => result.matches)),
    { initialValue: this.breakpointObserver.isMatched('(max-width: 767px)') },
  );

  protected readonly isMobile = computed(() => this.isNativeMobile() || this.isMobileViewport());

  protected onChatClick(): void {
    this.loggingService.logTrace('[AppComponent] Chat button clicked - calling servisBotService.open()');
    this.servisBotService.open();
  }

  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: '/' },
  );

  private cookieConsentInitialized = false;
  private readonly initCookieConsentOnNonOnboarding = effect(() => {
    this.initCookieConsent(this.currentUrl());
  });

  /**
   * OneTrust's auto-blocker only blocks categorized scripts that have not executed
   * yet, so consent is initialized ahead of analytics on eligible entry routes.
   */
  private initCookieConsent(url: string): void {
    if (this.cookieConsentInitialized || url === '/' || url.startsWith('/onboarding')) {
      return;
    }
    this.cookieConsentInitialized = true;
    this.cookieConsentService.initialize().catch(() => undefined);
  }

  /**
   * `ngOnInit` runs once per page load, so a user who signs in via client-side navigation
   * (the CIAM callback routing to `/dashboard`) never armed the refresh monitor or the idle
   * watcher: the token then lapsed with no proactive renewal and no warning until an API
   * call returned 401. Keying off `currentUrl()` re-checks on every navigation, which covers
   * every sign-in path because they all end in one, plus re-login after an expiry redirect.
   *
   * Both calls are safe to repeat — `startMonitoring()` early-returns while already running.
   */
  private readonly armSessionTimers = effect(() => {
    const url = this.currentUrl();

    if (!this.authService.verifyAuthState() || !this.isProtectedRoute(url)) {
      return;
    }

    this.tokenRefreshService.startMonitoring();

    if (!this.userIdleService.isWatching()) {
      this.userIdleService.startWatching();
    }
  });

  private readonly SERVISBOT_WHITELISTED_ROUTES = [
    '/loans/',
    '/contact-information',
    '/messages/',
    '/manage-paperless',
    '/unsubscribe-paperless',
    '/faqs',
    '/contact-us',
    '/help/',
  ];

  private isWhitelistedRoute(url: string): boolean {
    return this.SERVISBOT_WHITELISTED_ROUTES.some((route) => url.startsWith(route));
  }

  protected showChatCard = computed(() => {
    if (this.userIdleService.showWarning()) {
      return false;
    }
    if (!this.authService.isAuthenticated()) {
      return false;
    }
    if (this.tourGuideService.isActive()) {
      return false;
    }
    return this.isWhitelistedRoute(this.currentUrl());
  });

  private maintenanceRedirectEffect = effect(() => {
    if (this.isNativeMobile()) {
      return;
    }

    const isMaintenanceActive = this.firebaseService.isInitialized() && this.firebaseService.config().maintenance_mode;

    if (isMaintenanceActive && !this.currentUrl().startsWith('/underMaintenance')) {
      this.router.navigate(['/underMaintenance'], { replaceUrl: true });
    }
  });

  private servisBotVisibilityEffect = effect(() => {
    const shouldShow = this.showChatCard();

    if (this.userIdleService.isIdleLogout) {
      document.body.classList.remove('servisbot-enabled');
      return;
    }

    if (shouldShow) {
      document.body.classList.add('servisbot-enabled');
    } else {
      document.body.classList.remove('servisbot-enabled');
    }
  });

  ngOnInit(): void {
    this.themeService.initialize();

    if (this.platformService.isNativeMobile()) {
      this.disableZoomOnMobile();
    }

    if (this.platformService.isNativeMobile()) {
      SplashScreen.hide();
      this.statusBarService.setStyleLight();
      this.initializeOtaAndRemoteConfig();
    }

    this.loggingService.initialize();
    this.initCookieConsent(this.currentUrl());
    this.analyticsService.initialize();
    this.dynatraceService.initialize();
    this.firebaseService.initialize();
    this.crashlyticsService.initialize();
    this.nativeNavigationService.initialize();
    this.authService.initCrossTabSync();
    this.userIdleService.initialize(this.env);
    this.glanceService.initializeWebSdk();

    // The idle watcher and refresh monitor are armed by the `armSessionTimers` effect,
    // which also covers sign-in via client-side navigation.
    if (this.authService.verifyAuthState() && this.isProtectedRoute(this.currentUrl())) {
      this.initializeCiamIfActive();
    }

    // On iOS, returning from an external page (e.g., Docutech e-sign) reloads
    // the app at the root URL. Check for a saved return URL and redirect there
    // so the user lands back on the origination dashboard instead of the main dashboard.
    this.redirectToEsignReturnUrl();

    const handleVisibilityChange = () => {
      this.onBecameVisible().catch((error) =>
        this.loggingService.logException(error, '[App] Visibility revalidation failed'),
      );
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    if (this.platformService.isNativeMobile()) {
      this.setupNavigationKeyboardDismiss();
      this.setupNativeLinkInterceptor();
      this.tokenRefreshService.nativeSessionResumed
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.createBiometricLoginNote());
    }

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      this.userIdleService.stopWatching();
    });
  }

  /**
   * The token can expire while the tab is hidden, and no navigation happens when it
   * comes back — so `authGuard` never runs. Revalidate before letting the resumed
   * session keep rendering or refetching borrower data.
   */
  private async onBecameVisible(): Promise<void> {
    if (document.visibilityState !== 'visible') {
      return;
    }

    this.checkAuthAndRedirect();

    if (!this.authService.verifyAuthState() || !this.isProtectedRoute(this.currentUrl())) {
      return;
    }

    if (!(await this.tokenRefreshService.ensureValidSession())) {
      await this.handleExpiredSessionOnResume();
      return;
    }

    if (!this.userIdleService.isWatching()) {
      this.userIdleService.startWatching();
    }
  }

  private async handleExpiredSessionOnResume(): Promise<void> {
    const returnUrl = this.currentUrl();

    this.loggingService.logWarning('[App] Session expired while hidden, redirecting to login');

    this.userIdleService.stopWatching();
    this.tokenRefreshService.stopMonitoring();
    this.authService.clearAllTokens();

    await this.router.navigate(['/login'], {
      queryParams: { returnUrl, sessionExpired: 'true' },
      replaceUrl: true,
    });
  }

  private async createBiometricLoginNote(): Promise<void> {
    const userId = this.authService.getUserId();
    if (!userId) {
      this.tokenRefreshService.markNativeSessionResumeNoteHandled(false);
      return;
    }

    try {
      const profile = await this.profileService.getUserProfile(userId);
      if (!isValidUserProfile(profile) || !profile.firstName) {
        this.tokenRefreshService.markNativeSessionResumeNoteHandled(false);
        return;
      }

      await this.profileService.successfulLoginNote({
        username: profile.firstName,
        globalUserId: profile.globalUserID,
        channel: 'Mobile',
        loginMethod: 'Biometric',
      });
      this.tokenRefreshService.markNativeSessionResumeNoteHandled(true);
    } catch (error) {
      this.tokenRefreshService.markNativeSessionResumeNoteHandled(false);
      this.loggingService.logException(error, '[App] Biometric SuccessfulLoginNote failed');
    }
  }

  private readonly PROTECTED_ROUTE_PREFIXES = [
    '/dashboard',
    '/loans/',
    '/origination/',
    '/apply/',
    '/paperless',
    '/manage-paperless',
    '/autopay-consent',
    '/contact-information',
    '/messages',
    '/faqs',
  ];

  private isProtectedRoute(url: string): boolean {
    return this.PROTECTED_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix));
  }

  private redirectToEsignReturnUrl(): void {
    if (!this.platformService.isNativeMobile()) return;
    const returnUrl = localStorage.getItem('__ld_esign_return_url');
    if (!returnUrl) return;
    localStorage.removeItem('__ld_esign_return_url');
    if (this.authService.verifyAuthState()) {
      this.userIdleService.startWatching();
      this.tokenRefreshService.startMonitoring();
      this.initializeCiamIfActive();
      this.router.navigateByUrl(returnUrl, { replaceUrl: true });
    }
  }

  private checkAuthAndRedirect(): void {
    const isAuthenticated = this.authService.verifyAuthState();
    const url = this.currentUrl();

    if (!isAuthenticated && this.isProtectedRoute(url)) {
      this.router.navigate(['/login'], { replaceUrl: true });
    }
  }

  private setupNavigationKeyboardDismiss(): void {
    this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      if (this.isDialogFocused()) {
        return;
      }

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }

      setTimeout(() => {
        if (!this.isDialogFocused()) {
          void this.keyboardService.hide();
        }
      }, 300);
      setTimeout(() => {
        if (!this.isDialogFocused()) {
          void this.keyboardService.hide();
        }
      }, 800);
    });
  }

  private isDialogFocused(): boolean {
    const activeElement = document.activeElement;
    return activeElement instanceof HTMLElement && activeElement.closest('[role="dialog"]') !== null;
  }

  /**
   * On iOS (WKWebView / Capacitor), links with target="_blank" fail because
   * WKWebView does not support opening new windows. This global listener
   * intercepts all such clicks and routes them through the Capacitor Browser
   * plugin (for external URLs) or Angular Router (for internal routes).
   */
  private setupNativeLinkInterceptor(): void {
    const handleNativeLinkClick = (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a[target="_blank"]') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.href;
      if (!href) return;

      event.preventDefault();
      event.stopPropagation();

      // Internal route via routerLink generates capacitor://localhost/path on iOS
      if (href.startsWith('capacitor://localhost')) {
        const path = new URL(href).pathname;
        // Static assets (e.g. /assets/pdf/...) must open in the in-app browser,
        // not via Angular Router.
        if (path.startsWith('/assets/')) {
          this.browserService.openUrl(window.location.origin + path);
          return;
        }
        this.router.navigate([path]);
        return;
      }

      // External URLs (https://, http://)
      this.browserService.openUrl(href);
    };

    document.addEventListener('click', handleNativeLinkClick, true); // capture phase to intercept before Angular handlers
    this.destroyRef.onDestroy(() => document.removeEventListener('click', handleNativeLinkClick, true));
  }

  private disableZoomOnMobile(): void {
    const viewport = document.querySelector('meta[name="viewport"]');
    if (viewport) {
      viewport.setAttribute(
        'content',
        'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover',
      );
    }
  }

  private async initializeCiamIfActive(): Promise<void> {
    if (!this.env.appSettings.useCustomerIdentityAuth) {
      return;
    }

    if (localStorage.getItem('ciam_session_active') !== 'true') {
      return;
    }

    try {
      const storedConfig = sessionStorage.getItem('ciam_tenant_config');
      if (storedConfig) {
        const tenantConfig = JSON.parse(storedConfig);
        await this.ciamAuthService.initialize(tenantConfig);
        return;
      }

      const host =
        window.location.hostname === 'localhost'
          ? (this.env.appSettings.ciamTenantHost ?? 'consumerportal-dv1.loandepotdev.works')
          : window.location.hostname;
      const tenantConfig = await this.customerIdentityService.getTenantConfig(host);
      if (!tenantConfig?.ciamDomain) {
        return;
      }
      await this.ciamAuthService.initialize(tenantConfig);
    } catch (error) {
      this.loggingService.logException(
        error instanceof Error ? error : new Error('Failed to re-initialize CIAM auth service on app startup'),
      );
    }
  }

  private async initializeOtaAndRemoteConfig(): Promise<void> {
    try {
      // Initialize OTA first to confirm current bundle is healthy (notifyAppReady).
      // This MUST happen early to prevent the Capgo plugin from auto-rolling back.
      await this.otaUpdateService.initialize();
    } catch (error) {
      this.loggingService.logException(
        error instanceof Error ? error : new Error('App: Failed to initialize OTA updater'),
      );
    }

    try {
      // Initialize Remote Config (fetches Firebase values including ota_updates_enabled)
      await this.remoteConfigService.initialize();
      await this.forceUpdateService.checkForUpdate();

      // Check for OTA updates AFTER Remote Config is ready so the kill-switch works correctly
      this.otaUpdateService.checkForUpdate().catch((err) => {
        this.loggingService.logException(err instanceof Error ? err : new Error('OTA background update check failed'));
      });
    } catch (error) {
      this.loggingService.logException(
        error instanceof Error ? error : new Error('App: Failed to initialize remote config or check for update'),
      );
    }
  }
}
